import React, { useState, useContext } from "react";
import { Dialog, IconButton, Typography, withStyles } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import MuiDialogActions from "@material-ui/core/DialogActions";
import { LoadingIndicator } from "../../../../Component";
import {
  getEntityIdFromMdmId,
  getEntityIdFromDunsNumber,
  createNewEntity,
} from "../../../../API/Entity";
import ModalDialogContext from "../../../../Context/ModalDialog/ModalDialogContext";
import { SearchRecords } from "../../..";
import AssociationForm from "../AssociationForm";
import styles from "./styles";
import { useTranslation } from "react-i18next";
import CustomButton from "../../../../Component/CustomButton";

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: theme.spacing(1),
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1),
  },
}))(MuiDialogActions);

const CreateAssociationDialog = (props) => {
  const {
    classes,
    isDialogOpen,
    existingAssociations = [],
    allowedAssociations = [],
    onSubmit,
    onClose,
    user,
    hasPermission,
    searchQuery,
    defaultType,
    selectedRecord,
  } = props;

  const [isLoading, setLoading] = useState(false);
  const [targetEntityId, setTargetEntityId] = useState();
  const [targetMdmId, setTargetMdmId] = useState();
  const [targetDnbId, setTargetDnbId] = useState();
  const [targetRecordName, setTargetRecordName] = useState();
  const [associations, setAssociations] = useState([]);
  const [noAassociationError, setNoAassociationError] = useState(false);
  const [step, setStep] = useState(1);
  const [submiting, setSubmiting] = useState(false);
  const [hasAttributeError, setHasAttributeError] = useState(false);

  const { showErrorMessage } = useContext(ModalDialogContext);
  const { t } = useTranslation();

  const renderLoadingIndicator = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
        }}
      >
        <LoadingIndicator />
      </div>
    );
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const onRecordSelect = async (record, source) => {
    try {
      setLoading(true);
      let targetEntityId;
      let targetMdmId;
      let targetDnbId;
      if (source === "MDM") {
        const result = await getEntityIdFromMdmId(record.id);
        const { data } = result || {};
        if (data) {
          targetEntityId = data;
        } else {
          targetMdmId = record.id;
        }
      } else if (source === "DNB") {
        const result = await getEntityIdFromDunsNumber(record.dunsNumber);
        const { data } = result || {};
        if (data) {
          targetEntityId = data;
        } else {
          targetDnbId = record.dunsNumber;
        }
      } else if (source === "New") {
        const result = await createNewEntity(record);
        const { data } = result || {};
        targetEntityId = data.id;
      } else {
        targetEntityId = record.id;
      }
      if (targetEntityId) {
        const as = existingAssociations.find(
          (as) => as.entity.id === targetEntityId,
        );
        const { associations = [] } = as || {};
        const tas = JSON.parse(JSON.stringify(associations));
        setAssociations(tas);
      }
      setTargetEntityId(targetEntityId);
      setTargetMdmId(targetMdmId);
      setTargetDnbId(targetDnbId);
      setTargetRecordName(record.name);
      setStep(2);
      setLoading(false);
    } catch (error) {
      showErrorMessage({ error });
    }
  };

  const listAdditionalMetadata = (metadata, isValue) => {
    if (!metadata || Object.keys(metadata).length === 0) {
      return [];
    }
    const keysSorted = Object.keys(metadata).sort((a, b) => a > b);
    if (isValue) {
      return keysSorted.map((key) => metadata[key]);
    }
    return keysSorted;
  };

  const submit = async () => {
    if (hasAttributeError) {
      return;
    }
    if (associations.length === 0) {
      setNoAassociationError(true);
    } else {
      setNoAassociationError(false);
      setSubmiting(true);
      const updatedAssociations = associations.map((as) => {
        const { associationId, attributes } = as;
        return {
          id: associationId,
          attributes: attributes.map((attr) => {
            return {
              id: attr.id,
              dataType: attr.data_type,
              value: attr.user_value,
            };
          }),
        };
      });

      const additionalInformation = {
        documents: [],
      };

      if (selectedRecord) {
        const { documents = [] } = selectedRecord;

        for (let doc of documents) {
          additionalInformation.documents.push({
            category: doc.category,
            datasource: doc.datasource,
            link: doc.link,
            metadata: {
              keys: listAdditionalMetadata(doc.metadata, false),
              values: listAdditionalMetadata(doc.metadata, true),
            },
            mimeType: doc.mimeType,
            title: doc.title,
          });
        }
      }

      await onSubmit({
        targetMdmId,
        targetEntityId,
        targetDnbId,
        associations: updatedAssociations,
        additionalInformation,
      });
      setSubmiting(false);
    }
  };

  const searchRecordActions = [
    {
      iconClass: "far fa-plus",
      position: "row",
      tooltip: t("labels.record.search.table.actions.createAssociation"),
      callBack: onRecordSelect,
    },
  ];
  return (
    <Dialog
      disableBackdropClick
      onClose={handleClose}
      aria-labelledby="add-document-modal"
      open={isDialogOpen}
      fullWidth={true}
      maxWidth="xl"
      scroll={"paper"}
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle id="add-document-modal" onClose={handleClose}>
        <Typography className={classes.panelTitle}>
          {targetRecordName
            ? t("labels.record.associations.associationsWith", {
                recordName: targetRecordName,
              })
            : t("labels.record.associations.sectionHeader")}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          renderLoadingIndicator()
        ) : step === 1 ? (
          <SearchRecords
            recordActions={searchRecordActions}
            searchQuery={searchQuery}
            onNewRecordSubmit={onRecordSelect}
            dropTopMargin={true}
            user={user}
            hasPermission={hasPermission}
            hideCheckTypeSelection={false}
            disableCheckTypeSelection={true}
            defaultCheckType={"AML"}
            selectedRecord={selectedRecord}
          />
        ) : (
          <AssociationForm
            associations={associations}
            allowedAssociations={allowedAssociations}
            noAassociationError={noAassociationError}
            existingAssociations={existingAssociations}
            setHasAttributeError={setHasAttributeError}
            defaultType={defaultType}
          />
        )}
      </DialogContent>

      <DialogActions>
        <CustomButton 
          variant="secondary" 
          isDisabled={isLoading}
          onClick={handleClose}
        >
          {t("labels.common.cancel")}
        </CustomButton>
        {step === 2 && (
          <CustomButton 
            variant="primary" 
            isLoading={submiting}
            isDisabled={isLoading}
            onClick={submit}
          >
            {t("labels.common.create")}
          </CustomButton>

        )}
      </DialogActions>
    </Dialog>
  );
};

export default withStyles(styles, { withTheme: true })(CreateAssociationDialog);
